<template>
  <div class="ai-page">
    <header class="page-head">
      <div>
        <h2>生图管理</h2>
        <p>任务由服务器持续执行，关闭浏览器不会中断。</p>
      </div>
      <div class="head-actions">
        <span class="connection-state" :class="{ online: socketConnected }">
          {{ socketConnected ? "实时进度已连接" : "定时刷新中" }}
        </span>
        <button class="plain-button" type="button" @click="openConfig">接口配置</button>
        <button class="primary-button" type="button" @click="openFolderModal()">＋ 新建文件夹</button>
      </div>
    </header>

    <section class="generator-band">
      <div class="prompt-field">
        <label for="aiPrompt">画面描述</label>
        <textarea id="aiPrompt" v-model.trim="generateForm.prompt" maxlength="12000" placeholder="描述主体、界面布局、构图、材质、光线和细节要求……"></textarea>
        <span>{{ generateForm.prompt.length }}/12000</span>
      </div>
      <div class="generator-controls">
        <label><span>模型</span><input v-model.trim="generateForm.model" placeholder="gpt-image-2" /></label>
        <label><span>保存到</span><select v-model="generateForm.folderId"><option :value="null">未分类</option><option v-for="folder in folders" :key="folder.id" :value="folder.id">{{ folder.folderName }}</option></select></label>
        <label><span>画幅</span><select v-model="generateForm.size"><option value="auto">自动</option><optgroup label="方形"><option value="1024x1024">1024×1024</option><option value="2048x2048">2048×2048（2K）</option></optgroup><optgroup label="横版"><option value="1536x1024">1536×1024</option><option value="1440x1088">1440×1088</option><option value="2048x1152">2048×1152（2K）</option><option value="2048x1536">2048×1536（2K）</option><option value="3840x2160">3840×2160（4K）</option></optgroup><optgroup label="竖版"><option value="1024x1536">1024×1536</option><option value="1088x1440">1088×1440</option><option value="1152x2048">1152×2048（2K）</option><option value="1536x2048">1536×2048（2K）</option><option value="2160x3840">2160×3840（4K）</option></optgroup></select></label>
        <label><span>质量</span><select v-model="generateForm.quality"><option value="auto">自动</option><option value="low">低</option><option value="medium">中</option><option value="high">高</option></select></label>
        <label><span>数量</span><select v-model.number="generateForm.count"><option :value="1">1 张</option><option :value="2">2 张</option><option :value="3">3 张</option><option :value="4">4 张</option></select></label>
        <label><span>格式</span><select v-model="generateForm.outputFormat"><option value="png">PNG</option><option value="webp">WebP</option><option value="jpeg">JPEG</option></select></label>
        <label><span>背景</span><select v-model="generateForm.background"><option value="auto">自动</option><option value="opaque">不透明</option><option value="transparent">透明</option></select></label>
        <button class="generate-button" type="button" :disabled="submitting || !config.apiKeyConfigured" @click="submitTask">
          {{ submitting ? "提交中..." : config.apiKeyConfigured ? "开始生成" : "先配置接口" }}
        </button>
      </div>
    </section>

    <section v-if="activeTasks.length || recentTasks.length" class="task-strip">
      <header><strong>后台任务</strong><span>{{ activeTasks.length }} 个执行中</span></header>
      <div class="task-list">
        <article v-for="task in recentTasks" :key="task.id" class="task-item" :class="'task-item--' + task.status.toLowerCase()">
          <div class="task-main">
            <span class="task-status">{{ statusLabel(task.status) }}</span>
            <strong :title="task.prompt">{{ task.prompt }}</strong>
            <small>{{ task.model }} · {{ task.size }} · {{ task.completedCount }}/{{ task.requestedCount }}</small>
          </div>
          <div class="progress-track"><span :style="{ width: taskProgress(task) + '%' }"></span></div>
          <p v-if="task.errorMessage">{{ task.errorMessage }}</p>
        </article>
      </div>
    </section>

    <div class="library-layout">
      <aside class="folder-panel">
        <div class="folder-panel__head"><strong>文件夹</strong><span>{{ images.length }} 张</span></div>
        <button class="folder-row" :class="{ active: selectedFolderId === null }" type="button" @click="selectFolder(null)">
          <span class="folder-icon"></span><strong>全部图片</strong><small>{{ totalImageCount }}</small>
        </button>
        <div v-for="folder in folders" :key="folder.id" class="folder-entry">
          <button class="folder-row" :class="{ active: selectedFolderId === folder.id }" type="button" @click="selectFolder(folder.id)">
            <span class="folder-icon"></span><strong :title="folder.folderName">{{ folder.folderName }}</strong><small>{{ folder.imageCount || 0 }}</small>
          </button>
          <button class="folder-more" type="button" title="修改文件夹" @click="openFolderModal(folder)">✎</button>
        </div>
      </aside>

      <main class="image-library">
        <header class="library-head">
          <div><h3>{{ selectedFolderName }}</h3><p>{{ images.length }} 张服务器图片</p></div>
          <button class="plain-button" type="button" :disabled="loading" @click="loadWorkspace">刷新</button>
        </header>
        <div v-if="loading" class="page-state">正在读取生图库...</div>
        <section v-else-if="images.length" class="image-grid">
          <article v-for="image in images" :key="image.id" class="image-card">
            <button class="image-preview" type="button" @click="previewImage = image">
              <img :src="resolveAssetUrl(image.fileUrl)" :alt="image.fileName" loading="lazy" />
              <span>{{ image.widthPx && image.heightPx ? `${image.widthPx}×${image.heightPx}` : image.mimeType }}</span>
            </button>
            <div class="image-copy">
              <strong :title="image.prompt">{{ image.prompt }}</strong>
              <small>{{ image.model }} · {{ formatFileSize(image.fileSize) }} · {{ formatTime(image.createdAt) }}</small>
            </div>
            <div class="image-controls">
              <select :value="image.folderId || ''" aria-label="移动图片到文件夹" @change="moveImageToFolder(image, $event.target.value)">
                <option value="">未分类</option>
                <option v-for="folder in folders" :key="folder.id" :value="folder.id">{{ folder.folderName }}</option>
              </select>
              <button v-if="canDesignEdit" class="attach-button" type="button" @click="openAttachModal(image)">加入交付设计</button>
              <a :href="resolveAssetUrl(image.fileUrl)" :download="image.fileName">下载</a>
              <button class="delete-button" type="button" @click="removeImage(image)">删除</button>
            </div>
          </article>
        </section>
        <div v-else class="page-state page-state--empty">
          <strong>这个分类里还没有图片</strong>
          <span>在上方填写 Prompt 后提交后台生图任务。</span>
        </div>
      </main>
    </div>

    <div v-if="configModal" class="modal-backdrop" @click.self="configModal = false">
      <form class="modal-panel" @submit.prevent="saveConfig">
        <header><div><h3>生图接口配置</h3><p>配置保存在服务器，仅后台任务使用。</p></div><button type="button" @click="configModal = false">×</button></header>
        <label><span>API URL</span><input v-model.trim="configForm.baseUrl" type="url" required placeholder="https://api.example.com/v1" /></label>
        <label><span>API Key</span><input v-model.trim="configForm.apiKey" type="password" :placeholder="config.apiKeyConfigured ? `留空保持 ${config.apiKeyMask}` : 'sk-...'" /></label>
        <label><span>默认模型</span><input v-model.trim="configForm.model" required placeholder="gpt-image-2" /></label>
        <div v-if="configMessage" class="form-message" :class="{ error: configMessageError }">{{ configMessage }}</div>
        <footer><button class="plain-button" type="button" :disabled="testingConfig" @click="testConfig">{{ testingConfig ? "测试中..." : "测试连接" }}</button><button class="primary-button" type="submit" :disabled="savingConfig">{{ savingConfig ? "保存中..." : "保存配置" }}</button></footer>
      </form>
    </div>

    <div v-if="folderModal" class="modal-backdrop" @click.self="folderModal = false">
      <form class="modal-panel modal-panel--small" @submit.prevent="saveFolder">
        <header><h3>{{ folderForm.id ? "修改文件夹" : "新建文件夹" }}</h3><button type="button" @click="folderModal = false">×</button></header>
        <label><span>文件夹名称</span><input v-model.trim="folderForm.folderName" required maxlength="96" autofocus placeholder="例如：登录页方案" /></label>
        <footer><button v-if="folderForm.id" class="danger-outline" type="button" @click="removeFolder">删除文件夹</button><span v-else></span><button class="primary-button" type="submit" :disabled="savingFolder">{{ savingFolder ? "保存中..." : "保存" }}</button></footer>
      </form>
    </div>

    <div v-if="attachModal" class="modal-backdrop" @click.self="attachModal = false">
      <form class="modal-panel" @submit.prevent="attachImage">
        <header><div><h3>加入交付设计</h3><p>会复制一份独立文件，删除生图原件不会影响交付。</p></div><button type="button" @click="attachModal = false">×</button></header>
        <div class="target-tabs">
          <button type="button" :class="{ active: attachForm.targetKind === 'SCREEN' }" @click="attachForm.targetKind = 'SCREEN'">作为界面图</button>
          <button type="button" :class="{ active: attachForm.targetKind === 'ICON' }" @click="attachForm.targetKind = 'ICON'">作为界面图标</button>
        </div>
        <label><span>交付文件夹</span><select v-model="attachForm.projectId" required @change="loadAttachScreens"><option disabled :value="null">请选择</option><option v-for="project in designProjects" :key="project.id" :value="project.id">{{ project.projectName }}</option></select></label>
        <label v-if="attachForm.targetKind === 'ICON'"><span>关联界面</span><select v-model="attachForm.screenId" required><option disabled :value="null">请选择具体界面</option><option v-for="screen in designScreens" :key="screen.id" :value="screen.id">{{ screen.screenName }}</option></select></label>
        <label><span>显示名称</span><input v-model.trim="attachForm.displayName" maxlength="128" placeholder="例如：首页第一版" /></label>
        <footer><button class="plain-button" type="button" @click="attachModal = false">取消</button><button class="primary-button" type="submit" :disabled="attaching">{{ attaching ? "添加中..." : "确认添加" }}</button></footer>
      </form>
    </div>

    <div v-if="previewImage" class="lightbox" @click.self="previewImage = null">
      <header><strong>{{ previewImage.prompt }}</strong><button type="button" @click="previewImage = null">×</button></header>
      <img :src="resolveAssetUrl(previewImage.fileUrl)" :alt="previewImage.fileName" />
    </div>

    <div v-if="toast" class="toast" :class="{ error: toastError }">{{ toast }}</div>
  </div>
</template>

<script>
import {
  attachAiImage,
  createAiImageFolder,
  createAiImageTask,
  deleteAiImage,
  deleteAiImageFolder,
  fetchAiImageConfig,
  fetchAiImageWorkspace,
  moveAiImage,
  saveAiImageConfig,
  testAiImageConfig,
  updateAiImageFolder
} from "../api/aiImage"
import { fetchDesignProjects, fetchDesignWorkspace } from "../api/officeDesign"
import { hasAdminPermission } from "../utils/adminSession"
import { resolveApiOrigin, resolveBackendAssetUrl } from "../utils/apiEndpoint"

export default {
  name: "AiImageManagerView",
  data() {
    return {
      loading: false,
      refreshing: false,
      config: { baseUrl: "", model: "gpt-image-2", apiKeyConfigured: false, apiKeyMask: "" },
      folders: [],
      tasks: [],
      images: [],
      selectedFolderId: null,
      generateForm: { prompt: "", model: "gpt-image-2", folderId: null, size: "1024x1024", quality: "auto", count: 1, outputFormat: "png", background: "auto" },
      submitting: false,
      configModal: false,
      configForm: { baseUrl: "", apiKey: "", model: "gpt-image-2" },
      savingConfig: false,
      testingConfig: false,
      configMessage: "",
      configMessageError: false,
      folderModal: false,
      folderForm: { id: null, folderName: "" },
      savingFolder: false,
      attachModal: false,
      attachImageRecord: null,
      attachForm: { targetKind: "SCREEN", projectId: null, screenId: null, displayName: "" },
      designProjects: [],
      designScreens: [],
      attaching: false,
      previewImage: null,
      socket: null,
      socketConnected: false,
      socketRetryTimer: null,
      refreshTimer: null,
      toast: "",
      toastError: false,
      toastTimer: null,
      alive: true
    }
  },
  computed: {
    canDesignEdit() { return hasAdminPermission("office:design:edit") },
    activeTasks() { return this.tasks.filter(task => ["PENDING", "RUNNING"].includes(task.status)) },
    recentTasks() { return this.tasks.slice(0, 6) },
    totalImageCount() { return this.folders.reduce((sum, folder) => sum + Number(folder.imageCount || 0), 0) + this.images.filter(image => !image.folderId).length },
    selectedFolderName() {
      if (this.selectedFolderId === null) return "全部图片"
      const folder = this.folders.find(item => item.id === this.selectedFolderId)
      return folder ? folder.folderName : "图片"
    }
  },
  async mounted() {
    await this.loadWorkspace()
    this.connectProgress()
    this.refreshTimer = window.setInterval(() => this.silentRefresh(), 12000)
  },
  beforeDestroy() {
    this.alive = false
    clearInterval(this.refreshTimer)
    clearTimeout(this.socketRetryTimer)
    if (this.socket) this.socket.close()
  },
  methods: {
    resolveAssetUrl(value) { return resolveBackendAssetUrl(value) },
    async loadWorkspace() {
      this.loading = true
      try {
        const res = await fetchAiImageWorkspace(this.selectedFolderId)
        this.applyWorkspace(res.data || {})
      } catch (error) {
        this.showToast(error.message || "生图库读取失败", true)
      } finally {
        this.loading = false
      }
    },
    async silentRefresh() {
      if (this.refreshing) return
      this.refreshing = true
      try {
        const res = await fetchAiImageWorkspace(this.selectedFolderId)
        this.applyWorkspace(res.data || {})
      } catch (error) {
        // WebSocket断线时仍会继续定时尝试，不打断当前操作。
      } finally {
        this.refreshing = false
      }
    },
    applyWorkspace(data) {
      this.config = data.config || this.config
      this.folders = Array.isArray(data.folders) ? data.folders : []
      this.tasks = Array.isArray(data.tasks) ? data.tasks : []
      this.images = Array.isArray(data.images) ? data.images : []
      if (!this.generateForm.model || this.generateForm.model === "gpt-image-2") this.generateForm.model = this.config.model || "gpt-image-2"
    },
    connectProgress() {
      const token = localStorage.getItem("admin_token")
      if (!token || !this.alive) return
      const wsUrl = resolveApiOrigin().replace(/^http/i, "ws") + "/ws/admin/ai-images?token=" + encodeURIComponent(token)
      try {
        this.socket = new WebSocket(wsUrl)
        this.socket.onopen = () => { this.socketConnected = true }
        this.socket.onmessage = event => {
          try {
            const message = JSON.parse(event.data)
            if (message.type === "task-updated") this.silentRefresh()
          } catch (error) {
            // Ignore non-JSON progress frames.
          }
        }
        this.socket.onerror = () => { this.socketConnected = false }
        this.socket.onclose = () => {
          this.socketConnected = false
          if (this.alive) this.socketRetryTimer = window.setTimeout(() => this.connectProgress(), 5000)
        }
      } catch (error) {
        this.socketConnected = false
      }
    },
    async submitTask() {
      if (!this.generateForm.prompt) return this.showToast("请先填写画面描述", true)
      if (!this.config.apiKeyConfigured) return this.openConfig()
      this.submitting = true
      try {
        await createAiImageTask(this.generateForm)
        this.generateForm.prompt = ""
        await this.silentRefresh()
        this.showToast("任务已进入后台队列，可以关闭页面")
      } catch (error) {
        this.showToast(error.message || "任务提交失败", true)
      } finally {
        this.submitting = false
      }
    },
    async openConfig() {
      this.configMessage = ""
      try {
        const res = await fetchAiImageConfig()
        this.config = res.data || this.config
      } catch (error) {
        this.showToast(error.message || "配置读取失败", true)
      }
      this.configForm = { baseUrl: this.config.baseUrl || "", apiKey: "", model: this.config.model || "gpt-image-2" }
      this.configModal = true
    },
    async testConfig() {
      this.testingConfig = true
      this.configMessage = ""
      try {
        const res = await testAiImageConfig(this.configForm)
        this.configMessage = res.data || "连接成功"
        this.configMessageError = false
      } catch (error) {
        this.configMessage = error.message || "连接失败"
        this.configMessageError = true
      } finally {
        this.testingConfig = false
      }
    },
    async saveConfig() {
      this.savingConfig = true
      try {
        const res = await saveAiImageConfig(this.configForm)
        this.config = res.data || this.config
        this.generateForm.model = this.config.model || "gpt-image-2"
        this.configModal = false
        this.showToast("生图配置已保存")
      } catch (error) {
        this.configMessage = error.message || "配置保存失败"
        this.configMessageError = true
      } finally {
        this.savingConfig = false
      }
    },
    openFolderModal(folder) {
      this.folderForm = folder ? { id: folder.id, folderName: folder.folderName } : { id: null, folderName: "" }
      this.folderModal = true
    },
    async saveFolder() {
      if (!this.folderForm.folderName) return
      this.savingFolder = true
      try {
        if (this.folderForm.id) await updateAiImageFolder(this.folderForm.id, { folderName: this.folderForm.folderName })
        else await createAiImageFolder({ folderName: this.folderForm.folderName })
        this.folderModal = false
        await this.silentRefresh()
        this.showToast("文件夹已保存")
      } catch (error) {
        this.showToast(error.message || "文件夹保存失败", true)
      } finally {
        this.savingFolder = false
      }
    },
    async removeFolder() {
      if (!window.confirm(`删除文件夹“${this.folderForm.folderName}”吗？文件夹必须为空。`)) return
      try {
        await deleteAiImageFolder(this.folderForm.id)
        if (this.selectedFolderId === this.folderForm.id) this.selectedFolderId = null
        this.folderModal = false
        await this.loadWorkspace()
        this.showToast("文件夹已删除")
      } catch (error) {
        this.showToast(error.message || "文件夹删除失败", true)
      }
    },
    async selectFolder(folderId) {
      this.selectedFolderId = folderId
      await this.loadWorkspace()
    },
    async moveImageToFolder(image, value) {
      try {
        await moveAiImage(image.id, value ? Number(value) : null)
        await this.silentRefresh()
        this.showToast("图片已移动")
      } catch (error) {
        this.showToast(error.message || "图片移动失败", true)
        await this.silentRefresh()
      }
    },
    async removeImage(image) {
      if (!window.confirm("确认永久删除这张图片吗？数据库记录和服务器原文件都会删除，无法恢复。")) return
      try {
        await deleteAiImage(image.id)
        await this.silentRefresh()
        this.showToast("图片和服务器原文件已删除")
      } catch (error) {
        this.showToast(error.message || "图片删除失败", true)
      }
    },
    async openAttachModal(image) {
      this.attachImageRecord = image
      this.attachForm = { targetKind: "SCREEN", projectId: null, screenId: null, displayName: "AI生图-" + image.id }
      try {
        const res = await fetchDesignProjects()
        this.designProjects = Array.isArray(res.data) ? res.data : []
        const preferredId = Number(this.$route.query.projectId || 0)
        const preferred = this.designProjects.find(item => item.id === preferredId)
        this.attachForm.projectId = preferred ? preferred.id : this.designProjects.length ? this.designProjects[0].id : null
        await this.loadAttachScreens()
        this.attachModal = true
      } catch (error) {
        this.showToast(error.message || "交付设计读取失败", true)
      }
    },
    async loadAttachScreens() {
      this.attachForm.screenId = null
      this.designScreens = []
      if (!this.attachForm.projectId) return
      try {
        const res = await fetchDesignWorkspace(this.attachForm.projectId)
        this.designScreens = res.data && Array.isArray(res.data.screens) ? res.data.screens : []
      } catch (error) {
        this.showToast(error.message || "界面列表读取失败", true)
      }
    },
    async attachImage() {
      if (this.attachForm.targetKind === "ICON" && !this.attachForm.screenId) return this.showToast("请选择要关联的具体界面", true)
      this.attaching = true
      try {
        await attachAiImage(this.attachImageRecord.id, this.attachForm)
        this.attachModal = false
        this.showToast(this.attachForm.targetKind === "SCREEN" ? "已作为界面图加入交付设计" : "已关联为界面图标")
      } catch (error) {
        this.showToast(error.message || "加入交付设计失败", true)
      } finally {
        this.attaching = false
      }
    },
    taskProgress(task) {
      if (task.status === "SUCCEEDED") return 100
      if (!task.requestedCount) return 0
      return Math.max(task.status === "RUNNING" ? 8 : 0, Math.round(task.completedCount * 100 / task.requestedCount))
    },
    statusLabel(status) { return { PENDING: "排队中", RUNNING: "生成中", SUCCEEDED: "已完成", PARTIAL: "部分完成", FAILED: "失败" }[status] || status },
    formatFileSize(size) { return !size ? "0 KB" : size < 1024 * 1024 ? `${(size / 1024).toFixed(1)} KB` : `${(size / 1024 / 1024).toFixed(1)} MB` },
    formatTime(value) { return value ? String(value).replace("T", " ").slice(0, 16) : "" },
    showToast(message, error = false) {
      clearTimeout(this.toastTimer)
      this.toast = message
      this.toastError = error
      this.toastTimer = setTimeout(() => { this.toast = "" }, 3500)
    }
  }
}
</script>

<style scoped>
.ai-page { min-width: 0; color: #26323e; }
button, input, select, textarea { font: inherit; }
.page-head { min-height: 64px; display: flex; align-items: center; justify-content: space-between; gap: 20px; }
.page-head h2 { margin: 0; font-size: 20px; letter-spacing: 0; }
.page-head p { margin: 5px 0 0; color: #788490; font-size: 11px; }
.head-actions { display: flex; align-items: center; gap: 8px; }
.connection-state { color: #8a949d; font-size: 10px; }
.connection-state::before { content: ""; display: inline-block; width: 7px; height: 7px; margin-right: 5px; border-radius: 50%; background: #aeb7bf; }
.connection-state.online { color: #287358; }
.connection-state.online::before { background: #31926c; }
.primary-button, .plain-button, .generate-button, .attach-button { min-height: 36px; border-radius: 6px; padding: 0 13px; font-size: 11px; font-weight: 700; cursor: pointer; }
.primary-button, .generate-button { border: 0; background: #1f6b4f; color: #fff; }
.plain-button { border: 1px solid #d3dae0; background: #fff; color: #53606c; }
button:disabled { cursor: not-allowed; opacity: .55; }
.generator-band { display: grid; grid-template-columns: minmax(300px, 1.25fr) minmax(380px, 1fr); gap: 22px; padding: 20px 0; border-top: 1px solid #e4e8ec; border-bottom: 1px solid #e4e8ec; }
.prompt-field { position: relative; min-width: 0; }
.prompt-field label, .generator-controls label > span, .modal-panel label > span { display: block; margin-bottom: 6px; color: #52606b; font-size: 10px; font-weight: 700; }
.prompt-field textarea { width: 100%; min-height: 126px; resize: vertical; padding: 11px 12px 25px; border: 1px solid #d3dae0; border-radius: 6px; outline: 0; color: #28343e; line-height: 1.55; box-sizing: border-box; }
.prompt-field textarea:focus, input:focus, select:focus { border-color: #78a894; box-shadow: 0 0 0 2px rgba(31,107,79,.08); outline: 0; }
.prompt-field > span { position: absolute; right: 9px; bottom: 8px; color: #929ca5; font-size: 9px; }
.generator-controls { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; align-content: end; }
.generator-controls label { min-width: 0; }
.generator-controls input, .generator-controls select, .modal-panel input, .modal-panel select { width: 100%; height: 36px; padding: 0 9px; border: 1px solid #d3dae0; border-radius: 6px; background: #fff; color: #2d3943; box-sizing: border-box; font-size: 10px; }
.generate-button { align-self: end; height: 36px; }
.task-strip { padding: 16px 0; border-bottom: 1px solid #e4e8ec; }
.task-strip > header { display: flex; align-items: baseline; gap: 8px; margin-bottom: 9px; }
.task-strip > header strong { font-size: 12px; }
.task-strip > header span { color: #89949d; font-size: 9px; }
.task-list { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 9px; }
.task-item { min-width: 0; padding: 10px; border: 1px solid #dfe5e9; border-radius: 6px; background: #fff; }
.task-main { min-width: 0; display: grid; grid-template-columns: auto minmax(0, 1fr); gap: 4px 7px; align-items: center; }
.task-main strong { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 10px; }
.task-main small { grid-column: 2; color: #89949d; font-size: 8px; }
.task-status { grid-row: 1 / 3; padding: 4px 6px; border-radius: 4px; background: #edf4f1; color: #286b52; font-size: 8px; font-weight: 700; }
.task-item--failed .task-status { background: #fff0ef; color: #a7463c; }
.progress-track { height: 3px; margin-top: 8px; overflow: hidden; border-radius: 2px; background: #edf0f2; }
.progress-track span { display: block; height: 100%; background: #2f8564; transition: width .25s; }
.task-item p { margin: 7px 0 0; overflow: hidden; color: #a7463c; text-overflow: ellipsis; white-space: nowrap; font-size: 8px; }
.library-layout { min-height: 480px; display: grid; grid-template-columns: 210px minmax(0, 1fr); }
.folder-panel { min-width: 0; padding: 18px 14px 20px 0; border-right: 1px solid #e4e8ec; }
.folder-panel__head { display: flex; justify-content: space-between; margin: 0 8px 10px; }
.folder-panel__head strong { font-size: 11px; }
.folder-panel__head span { color: #8b959e; font-size: 9px; }
.folder-entry { position: relative; }
.folder-row { width: 100%; min-width: 0; height: 38px; display: grid; grid-template-columns: 20px minmax(0, 1fr) auto; align-items: center; gap: 7px; padding: 0 30px 0 8px; border: 0; border-radius: 5px; background: transparent; color: #596670; text-align: left; cursor: pointer; }
.folder-row:hover, .folder-row.active { background: #edf4f1; color: #245f49; }
.folder-row strong { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 10px; }
.folder-row small { font-size: 8px; }
.folder-icon { position: relative; width: 17px; height: 13px; border-radius: 2px; background: #d9a839; }
.folder-icon::before { content: ""; position: absolute; left: 0; top: -3px; width: 8px; height: 4px; border-radius: 2px 2px 0 0; background: inherit; }
.folder-more { position: absolute; top: 5px; right: 4px; width: 28px; height: 28px; border: 0; border-radius: 4px; background: transparent; color: #7a8690; cursor: pointer; }
.folder-more:hover { background: #ddeae4; color: #245f49; }
.image-library { min-width: 0; padding: 18px 0 28px 20px; }
.library-head { display: flex; align-items: center; justify-content: space-between; gap: 16px; margin-bottom: 12px; }
.library-head h3 { margin: 0; font-size: 14px; }
.library-head p { margin: 3px 0 0; color: #89949d; font-size: 9px; }
.image-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(210px, 1fr)); gap: 13px; }
.image-card { min-width: 0; overflow: hidden; border: 1px solid #dfe4e8; border-radius: 7px; background: #fff; }
.image-preview { position: relative; width: 100%; aspect-ratio: 1 / 1; display: grid; place-items: center; overflow: hidden; padding: 0; border: 0; background: #eef1f3; cursor: zoom-in; }
.image-preview img { width: 100%; height: 100%; object-fit: contain; }
.image-preview span { position: absolute; right: 7px; bottom: 7px; padding: 4px 6px; border-radius: 4px; background: rgba(31,39,45,.76); color: #fff; font-size: 8px; }
.image-copy { min-width: 0; padding: 10px 10px 7px; }
.image-copy strong, .image-copy small { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.image-copy strong { font-size: 10px; }
.image-copy small { margin-top: 4px; color: #89949d; font-size: 8px; }
.image-controls { display: grid; grid-template-columns: 1fr 1fr; gap: 5px; padding: 0 10px 10px; }
.image-controls select { min-width: 0; height: 29px; border: 1px solid #d7dee3; border-radius: 5px; color: #596670; font-size: 8px; }
.image-controls button, .image-controls a { min-height: 29px; display: grid; place-items: center; padding: 0 7px; border-radius: 5px; font-size: 8px; text-decoration: none; box-sizing: border-box; }
.attach-button { min-height: 29px; border: 1px solid #b9d0c6; background: #f3f8f6; color: #27684f; }
.image-controls a { border: 1px solid #d7dee3; color: #596670; }
.delete-button { border: 1px solid #e7c8c4; background: #fff7f6; color: #a9483e; cursor: pointer; }
.page-state { min-height: 300px; display: grid; place-items: center; color: #7f8a95; font-size: 11px; }
.page-state--empty { align-content: center; gap: 8px; border: 1px dashed #ccd4da; border-radius: 7px; background: #fafbfb; }
.page-state--empty strong { color: #53606c; font-size: 13px; }
.modal-backdrop { position: fixed; inset: 0; z-index: 95; display: grid; place-items: center; padding: 18px; background: rgba(22,29,36,.46); }
.modal-panel { width: min(480px, 100%); padding: 20px; border-radius: 8px; background: #fff; box-shadow: 0 22px 60px rgba(12,18,24,.2); }
.modal-panel--small { width: min(390px, 100%); }
.modal-panel header { display: flex; align-items: flex-start; justify-content: space-between; gap: 14px; margin-bottom: 17px; }
.modal-panel header h3 { margin: 0; font-size: 15px; }
.modal-panel header p { margin: 4px 0 0; color: #87919a; font-size: 9px; }
.modal-panel header > button { width: 32px; height: 32px; border: 0; border-radius: 5px; background: #f1f3f4; color: #68747f; font-size: 20px; cursor: pointer; }
.modal-panel > label { display: block; margin-top: 12px; }
.modal-panel footer { display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-top: 20px; }
.form-message { margin-top: 12px; padding: 9px 10px; border-left: 3px solid #2d8663; background: #edf7f2; color: #28684f; font-size: 9px; }
.form-message.error { border-color: #b75449; background: #fff2f1; color: #994239; }
.danger-outline { height: 36px; border: 1px solid #e1bbb6; border-radius: 6px; background: #fff; color: #a7483e; font-size: 10px; cursor: pointer; }
.target-tabs { display: grid; grid-template-columns: 1fr 1fr; gap: 4px; padding: 4px; border-radius: 6px; background: #e9edef; }
.target-tabs button { height: 32px; border: 0; border-radius: 4px; background: transparent; color: #65717d; font-size: 10px; cursor: pointer; }
.target-tabs button.active { background: #fff; color: #245f49; font-weight: 700; box-shadow: 0 1px 3px rgba(24,35,30,.1); }
.lightbox { position: fixed; inset: 0; z-index: 100; display: flex; flex-direction: column; background: rgba(14,19,23,.94); }
.lightbox header { min-height: 52px; display: flex; align-items: center; justify-content: space-between; gap: 20px; padding: 0 18px; color: #fff; }
.lightbox header strong { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 11px; }
.lightbox header button { width: 34px; height: 34px; border: 0; background: transparent; color: #fff; font-size: 24px; cursor: pointer; }
.lightbox img { min-height: 0; max-width: calc(100vw - 36px); max-height: calc(100vh - 76px); margin: auto; object-fit: contain; }
.toast { position: fixed; right: 22px; bottom: 22px; z-index: 120; max-width: min(420px, calc(100vw - 44px)); padding: 11px 14px; border-radius: 6px; background: #235f49; color: #fff; box-shadow: 0 8px 25px rgba(20,38,31,.2); font-size: 10px; }
.toast.error { background: #a8473d; }
@media (max-width: 980px) {
  .generator-band { grid-template-columns: 1fr; }
  .task-list { grid-template-columns: 1fr 1fr; }
  .library-layout { grid-template-columns: 170px minmax(0, 1fr); }
}
@media (max-width: 720px) {
  .page-head, .head-actions { align-items: flex-start; flex-direction: column; }
  .head-actions { width: 100%; }
  .generator-controls { grid-template-columns: 1fr 1fr; }
  .task-list { grid-template-columns: 1fr; }
  .library-layout { display: block; }
  .folder-panel { border-right: 0; border-bottom: 1px solid #e4e8ec; }
  .image-library { padding-left: 0; }
  .image-grid { grid-template-columns: 1fr; }
}
</style>
